import { Alert, Box, Button, Typography } from '@mui/material';
import React from 'react';

import { useSubscriptionCheckout } from '../hooks/useSubscriptionCheckout';
import { formatPriceCents, getProduct } from '../subscriptions/catalog';
import type { ProductId } from '../subscriptions/types';

export interface SubscriptionPurchaseCtaProps {
  assignmentId: number;
  productId: ProductId;
  returnTo?: string;
  compact?: boolean;
}

const SubscriptionPurchaseCta: React.FC<SubscriptionPurchaseCtaProps> = ({
  assignmentId,
  productId,
  returnTo,
  compact = false,
}) => {
  const product = getProduct(productId);
  const { loading, error, clearError, startCheckout } = useSubscriptionCheckout();

  if (!product) {
    return null;
  }

  const handleBuy = async () => {
    clearError();
    await startCheckout({ assignmentId, productId, returnTo });
  };

  const handleRetry = () => {
    void handleBuy();
  };

  return (
    <Box
      sx={{
        p: compact ? 1.5 : 2,
        borderRadius: 1,
        border: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
      data-testid="subscription-purchase-cta"
    >
      <Typography variant={compact ? 'subtitle2' : 'subtitle1'} gutterBottom>
        Next step: {product.name}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        {formatPriceCents(product.price_cents)} one-time purchase
      </Typography>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 1.5 }}
          action={
            error.retryable ? (
              <Button color="inherit" size="small" onClick={handleRetry} disabled={loading}>
                Try again
              </Button>
            ) : undefined
          }
        >
          <Typography variant="body2">{error.message}</Typography>
          <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
            {error.instruction}
          </Typography>
        </Alert>
      )}

      <Button
        variant="contained"
        color="secondary"
        onClick={() => void handleBuy()}
        disabled={loading}
        data-attr="subscription-purchase_buy"
      >
        {loading ? 'Starting checkout…' : `Buy ${product.name}`}
      </Button>
    </Box>
  );
};

export default SubscriptionPurchaseCta;
